/**
 * 腾讯广告 → DashBoard（操作原因聚合展示；CLAUDE.md §前端 UI 须借鉴 LEO v1.0，
 * 本页复用 leo-* 类）。
 *
 * 已读 leo-frontend-design.md v1.0，适用章节：§4.2 滚动内容页、§6.3 表单控件、
 * §6.5 表格/列表、§7.2 反馈原则、§7.3 日期格式（YYYY-MM-DD）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/tencent/auto-ad-operate-result/dashboard（默认过滤：
 *     操作日期起 = 本月 1 号、止 = 今天）。
 *   - 筛选：ds 起止(date，「操作日期 起」最早 2026-07-31) / 操作原因模糊；
 *     点「查询」提交；「重置」恢复默认值（本月 1 号 ~ 今天）并立即重查。
 *   - 校验：「操作日期 止」不得早于「操作日期 起」——止输入框 min 随起值联动，
 *     提交前再校验（不合法 toast 报错且不发请求）。
 *   - 排序：纯前端本地排序（未排 → 升序 → 降序 → 复原，同华为策略配置页模式）；
 *     操作原因按文本 localeCompare，其余命中量列按数值比较。
 *   - 数据来源：t_tencent_auto_ad_operate_result 按 model_condition GROUP BY 聚合
 *     （后端固定 status IN ('success', '—')；「操作原因」列完整展示不截断）。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type AutoAdOperateDashboardQuery,
  type AutoAdOperateDashboardRecord,
  type AutoAdOperateDashboardResult,
  getAutoAdOperateDashboard,
} from "../../services/tencent/autoAdOperateResult";
import { pushToast } from "../../hooks/useToast";

/** 筛选表单输入态（空串表示「不限」）。 */
interface DashboardFilters {
  ds_start: string;
  ds_end: string;
  condition: string;
}

/** 今天（yyyy-MM-dd，本地时区）。 */
function todayStr(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${m}-${d}`;
}

/** 本月 1 号（yyyy-MM-dd，本地时区）。 */
function firstDayOfMonth(): string {
  const now = new Date();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  return `${now.getFullYear()}-${m}-01`;
}

/** 默认筛选：操作日期起 = 本月 1 号、止 = 今天；操作原因不限（初次进入 / 重置共用）。 */
function defaultFilters(): DashboardFilters {
  return {
    ds_start: firstDayOfMonth(),
    ds_end: todayStr(),
    condition: "",
  };
}

/** 「操作日期 起」最早可选日期（表内数据起点 2026-07-31）。 */
const DS_MIN = "2026-07-31";

/** 可排序列（field 对应聚合行属性名；model_condition 文本比较，其余数值比较）。 */
type SortField =
  | "model_condition"
  | "data_cnt"
  | "yike_cnt"
  | "yunrui_cnt"
  | "pairui_cnt"
  | "liou_cnt";

/** 排序列定义（field 对应聚合行属性名，label 为表头文案）。 */
const SORT_FIELDS: { field: SortField; label: string }[] = [
  { field: "model_condition", label: "操作原因" },
  { field: "data_cnt", label: "命中量" },
  { field: "yike_cnt", label: "亿科命中量" },
  { field: "yunrui_cnt", label: "云锐命中量" },
  { field: "pairui_cnt", label: "派瑞命中量" },
  { field: "liou_cnt", label: "利欧命中量" },
];

/** 筛选表单 → 查询入参（空串剔除为 undefined，不参与过滤）。 */
function filtersToQuery(f: DashboardFilters): AutoAdOperateDashboardQuery {
  const q: AutoAdOperateDashboardQuery = {};
  if (f.ds_start) q.ds_start = f.ds_start;
  if (f.ds_end) q.ds_end = f.ds_end;
  if (f.condition) q.condition = f.condition;
  return q;
}

export default function AutoAdOperateDashboardPage() {
  const [filters, setFilters] = useState<DashboardFilters>(defaultFilters);
  const [data, setData] = useState<AutoAdOperateDashboardResult | null>(null);
  const [loading, setLoading] = useState(true);
  /** 当前排序列（null = 恢复服务端返回序）。 */
  const [sortField, setSortField] = useState<SortField | null>(null);
  /** 排序方向（sortField 为 null 时无意义）。 */
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const reload = useCallback(async (f: DashboardFilters) => {
    setLoading(true);
    try {
      const result = await getAutoAdOperateDashboard(filtersToQuery(f));
      setData(result);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "拉取操作原因聚合数据失败";
      pushToast(msg, "error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // 进入页面：按默认过滤加载一次（本月 1 号 ~ 今天 / 不限操作原因）。
    void reload(defaultFilters());
  }, [reload]);

  /** 表头点击排序循环：未排 → 升序 → 降序 → 取消（恢复服务端返回序）。 */
  function onSortHeader(field: SortField) {
    if (sortField !== field) {
      setSortField(field);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortField(null);
      setSortDir("asc");
    }
  }

  function onSearch() {
    if (filters.ds_start && filters.ds_end && filters.ds_end < filters.ds_start) {
      // 「止」早于「起」：阻止提交并提示（输入框 min 联动为第一道防线，此处兜底）。
      pushToast("「操作日期 止」不能早于「操作日期 起」", "error");
      return;
    }
    void reload(filters);
  }

  function onReset() {
    const defaults = defaultFilters();
    setFilters(defaults);
    // 重置后立即查询（filters 状态异步更新，手动传默认过滤）。
    void reload(defaults);
  }

  function updateFilter<K extends keyof DashboardFilters>(key: K, value: string) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  /** 排序后的可见行（操作原因文本 localeCompare / 命中量数值比较）。 */
  const visibleRows: AutoAdOperateDashboardRecord[] = useMemo(() => {
    const items = data?.items ?? [];
    if (sortField === null) return items;
    const dir = sortDir === "asc" ? 1 : -1;
    const field = sortField;
    return [...items].sort((a, b) => {
      if (field === "model_condition") {
        return a.model_condition.localeCompare(b.model_condition, "zh-CN") * dir;
      }
      return (a[field] - b[field]) * dir;
    });
  }, [data, sortField, sortDir]);

  /** 可排序列表头（点击循环升/降/复原，箭头指示当前状态）。 */
  function renderSortTh(field: SortField, label: string) {
    const active = sortField === field;
    return (
      <th className="border-b border-slate-200 px-3 py-2 text-left font-medium">
        <button
          type="button"
          onClick={() => onSortHeader(field)}
          title="点击排序：升序 → 降序 → 复原"
          className="inline-flex items-center gap-1 transition-colors hover:text-text-strong"
        >
          {label}
          <span className={active ? "text-accent-cta" : "text-text-muted-weak"}>
            {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </button>
      </th>
    );
  }

  const rowCnt = data?.items.length ?? 0;

  return (
    <div className="mx-auto max-w-[1280px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5">
        <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
          DashBoard
        </h1>
        <p className="mt-1 text-[12.5px] text-text-muted">
          腾讯广告策略执行结果按操作原因聚合（t_tencent_auto_ad_operate_result，仅统计
          status 为 success / — 的记录）；支持按操作日期、操作原因模糊筛选，命中量含
          亿科 / 云锐 / 派瑞 / 利欧分代理口径。
        </p>
      </div>

      {/* 筛选条 */}
      <div className="leo-card mb-4 rounded-xl p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              操作日期 起
            </label>
            <input
              type="date"
              className="leo-input w-[150px]"
              min={DS_MIN}
              value={filters.ds_start}
              onChange={(e) => updateFilter("ds_start", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              操作日期 止
            </label>
            <input
              type="date"
              className="leo-input w-[150px]"
              min={filters.ds_start || DS_MIN}
              value={filters.ds_end}
              onChange={(e) => updateFilter("ds_end", e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.05em] text-text-muted-weak">
              操作原因
            </label>
            <input
              type="text"
              className="leo-input w-[200px]"
              placeholder="模糊匹配"
              value={filters.condition}
              onChange={(e) => updateFilter("condition", e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSearch}
              disabled={loading}
              className="leo-cta-primary w-auto px-7"
            >
              {loading ? "查询中..." : "查询"}
            </button>
            <button
              type="button"
              onClick={onReset}
              disabled={loading}
              className="leo-btn-ghost"
            >
              重置
            </button>
          </div>
        </div>
      </div>

      {/* 聚合结果表 */}
      <div className="leo-card rounded-xl p-4">
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div className="text-[13px] text-text-muted">加载中…</div>
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div className="text-[13px] text-text-muted">无操作原因聚合数据</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12px]">
              <thead>
                <tr className="border-b border-slate-200 text-text-muted">
                  {SORT_FIELDS.map(({ field, label }) => renderSortTh(field, label))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, idx) => (
                  <tr
                    key={`${r.model_condition}-${idx}`}
                    className="border-b border-slate-200 align-top text-text-strong"
                  >
                    {/* 操作原因列：完整展示（换行不截断，不加 line-clamp / max-w 限制） */}
                    <td className="px-3 py-2 text-text-body">
                      <div className="break-words whitespace-normal">
                        {r.model_condition || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-text-emphasis">
                      {r.data_cnt}
                    </td>
                    <td className="px-3 py-2 font-mono text-text-emphasis">
                      {r.yike_cnt}
                    </td>
                    <td className="px-3 py-2 font-mono text-text-emphasis">
                      {r.yunrui_cnt}
                    </td>
                    <td className="px-3 py-2 font-mono text-text-emphasis">
                      {r.pairui_cnt}
                    </td>
                    <td className="px-3 py-2 font-mono text-text-emphasis">
                      {r.liou_cnt}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 text-[12px] text-text-muted">
              共 <span className="text-text-emphasis">{rowCnt}</span> 类操作原因
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
